'use client';
import { useLangueArrivee } from '@/app/lib/use-langue-arrivee';
import { LB } from '@/app/lib/lois-belges';
import { SLA_DISPONIBILITE } from '@/app/lib/conditions-commerciales';

// ═══════════════════════════════════════════════════════════════════════════
// CGU — CONDITIONS GÉNÉRALES D'UTILISATION (créées le 25/09/2026)
// ───────────────────────────────────────────────────────────────────────────
// Distinctes des deux documents voisins, qui ne couvrent PAS le même public :
//   • app/legal/cgv/page.js — le CONTRAT COMMERCIAL avec l'entité Cliente qui
//     souscrit (tarifs, durée, résiliation du contrat, mandat ONSS).
//   • app/legal/cgu-employe/page.js — les travailleurs du Client, en lecture
//     seule sur leurs propres fiches de paie.
// Ici : les UTILISATEURS que le Client habilite à opérer l'application au
// quotidien (RH, comptable, gérant…) — un compte individuel, pas l'entité qui
// a signé le contrat. Avant ce fichier, le lien « CGU » du footer public
// pointait vers app/legal/cgv (audit du 24/09/2026, point 6) : un visiteur
// qui cherchait les règles d'usage de la plateforme tombait sur les
// conditions de vente — même sujet, mauvais document.

const CONTENT = {
  fr: {
    title: 'Conditions générales d\'utilisation',
    subtitle: 'Applicables aux utilisateurs individuels habilités par une entreprise cliente à opérer la plateforme Aureus Social Pro (RH, comptable, gérant…)',
    updated: 'Version 1.0 — 25 septembre 2026',
    intro: 'En bref : si votre entreprise a souscrit à Aureus Social Pro, ces conditions régissent VOTRE compte personnel sur la plateforme — pas le contrat commercial de votre entreprise (voir les CGV) ni le portail employé en lecture seule (voir les CGU employé).',
    s1_title: '1. Champ d\'application',
    s1_p1: 'Les présentes conditions générales d\'utilisation (CGU) sont éditées par Aureus IA SRL, BCE BE 1028.230.781, Square Eugène Plasky 92-94 bte 11, 1030 Schaerbeek, Bruxelles. Email : info@aureussocial.be.',
    s1_p2: 'Elles s\'appliquent à toute personne physique (« Utilisateur ») disposant d\'un compte individuel sur app.aureussocial.be dans le cadre de son activité professionnelle pour le compte d\'une entreprise ayant souscrit au Service (le « Client »), au sens des Conditions Générales de Vente. Le Client reste seul signataire du contrat commercial ; les présentes CGU régissent la relation entre Aureus et chaque Utilisateur individuellement.',
    s1_p3: 'Un travailleur du Client accédant uniquement à son propre espace personnel (fiches de paie, congés) relève des Conditions d\'utilisation — Portail Employé, pas des présentes CGU.',
    s2_title: '2. Compte et accès',
    s2_p1: 'Le compte de l\'Utilisateur est créé par un administrateur du Client, qui définit son rôle et ses permissions (RH, comptable, lecture seule…). L\'Utilisateur est responsable de la confidentialité de ses identifiants et de toute action effectuée depuis son compte.',
    s2_p2: 'L\'authentification à deux facteurs (2FA) est disponible et fortement recommandée pour tout compte ayant accès à des données sensibles (NISS, IBAN, salaires). En cas de perte d\'accès ou de suspicion de compromission, contactez immédiatement l\'administrateur de votre organisation ou info@aureussocial.be.',
    s2_p3: 'Les permissions de l\'Utilisateur reflètent le rôle qui lui est attribué par le Client. Aureus n\'arbitre pas la répartition des rôles au sein du Client : celle-ci relève de l\'organisation interne du Client.',
    s3_title: '3. Usage autorisé',
    s3_li: [
      'Utiliser la plateforme dans le cadre de sa mission professionnelle pour le compte du Client',
      'Saisir des données exactes et à jour, notamment dans les déclarations sociales',
      'Respecter le périmètre du rôle qui lui a été attribué',
      'Signaler à l\'administrateur du Client ou à Aureus toute anomalie de fonctionnement ou de sécurité',
    ],
    s4_title: '4. Usages interdits',
    s4_li: [
      'Contourner ou tenter de contourner les mesures de sécurité (2FA, isolation des dossiers, journalisation)',
      'Introduire, ou demander à Aureus d\'introduire, une déclaration sociale antidatée ou que l\'Utilisateur sait inexacte — voir l\'Article 9 des CGV, qui s\'applique identiquement ici',
      'Accéder ou tenter d\'accéder aux données d\'un dossier auquel l\'Utilisateur n\'est pas habilité, y compris d\'autres Clients',
      'Extraire massivement des données par un moyen autre que les fonctions d\'export prévues par la plateforme',
      'Partager ses identifiants avec un tiers, y compris un autre membre du Client',
      'Utiliser la plateforme à des fins autres que la gestion de paie et RH du Client',
    ],
    s5_title: '5. Propriété intellectuelle du logiciel',
    s5_p1: 'La plateforme, son code source, son architecture, ses algorithmes de calcul, sa base de données, ses interfaces et sa documentation restent la propriété exclusive d\'Aureus IA SRL, protégée par le droit d\'auteur belge (Livre XI du Code de droit économique) et la directive 2009/24/CE relative à la protection juridique des programmes d\'ordinateur, acquise automatiquement dès la création, sans formalité de dépôt.',
    s5_p2: 'L\'accès donné à l\'Utilisateur est un droit d\'usage personnel, non exclusif et non cessible, limité à sa mission pour le compte du Client. L\'Utilisateur ne peut ni reproduire, ni décompiler, ni modifier, ni redistribuer le logiciel, ni en extraire le code source.',
    s5_p3: 'Cette propriété intellectuelle du logiciel est distincte de la propriété des données du Client (voir les CGV, Article 6) : les données saisies appartiennent au Client ; le logiciel qui les traite appartient à Aureus.',
    s6_title: '6. Données personnelles de l\'Utilisateur',
    s6_p1: 'Les données de connexion et d\'usage de l\'Utilisateur (identifiant, journal d\'accès) sont traitées conformément à la Politique de confidentialité. Pour les données salariales qu\'il consulte ou saisit au nom du Client, le Responsable du traitement est le Client ; Aureus agit en qualité de sous-traitant au sens de l\'Art. 28 RGPD.',
    s6_p2: `Conservation : le journal d'accès et d'audit est conservé selon la durée de conservation des données auxquelles il se rapporte — jusqu'à ${LB.conservation.fiscaleAns} ans pour ce qui touche aux données fiscales et salariales (Art. 315 CIR 1992, modifié par la loi du 20/11/2022).`,
    s7_title: '7. Sécurité',
    s7_li: [
      'Chiffrement AES-256-GCM des données sensibles (NISS, IBAN)',
      'Authentification forte 2FA disponible',
      'Isolation des données par dossier (Row Level Security)',
      'Journal d\'accès complet (audit trail)',
      'Sauvegardes chiffrées quotidiennes',
      'Hébergement exclusif Union européenne (Francfort, Allemagne)',
    ],
    s8_title: '8. Disponibilité',
    s8_p: `Aureus Social Pro vise une disponibilité de ${SLA_DISPONIBILITE} en moyenne mensuelle (hors maintenance planifiée, communiquée 48h à l'avance par email). En cas de panne, contactez l'administrateur de votre organisation ou info@aureussocial.be.`,
    s9_title: '9. Suspension et fin d\'accès individuel',
    s9_p1: 'L\'accès d\'un Utilisateur peut être retiré à tout moment par un administrateur du Client (départ, changement de rôle) — ce retrait ne met pas fin au contrat du Client, régi par les CGV.',
    s9_p2: 'Aureus peut suspendre l\'accès d\'un Utilisateur, indépendamment du contrat du Client, en cas d\'usage frauduleux avéré, de violation grave des présentes CGU ou de tentative d\'accès non autorisé à un autre dossier, après signalement à l\'administrateur du Client dans la mesure du possible.',
    s10_title: '10. Modifications',
    s10_p: 'Les présentes CGU peuvent être modifiées pour refléter des évolutions légales ou techniques. Toute modification substantielle est notifiée par email aux administrateurs du Client au moins 30 jours avant son entrée en vigueur.',
    s11_title: '11. Réclamations et droit applicable',
    s11_p1: 'Réclamation : adressez-vous à l\'administrateur de votre organisation ou à info@aureussocial.be. Réponse sous 30 jours maximum.',
    s11_p2: 'Autorité de Protection des Données (APD) : Rue de la Presse 35, 1000 Bruxelles · +32 2 274 48 00 · contact@apd-gba.be.',
    s11_p3: 'Droit applicable : droit belge. Tribunaux de l\'arrondissement judiciaire de Bruxelles.',
    footer: 'Document généré le 25 septembre 2026 — Version 1.0',
  },
  nl: {
    title: 'Algemene gebruiksvoorwaarden',
    subtitle: 'Van toepassing op individuele gebruikers die door een klant-onderneming gemachtigd zijn om het platform Aureus Social Pro te bedienen (HR, boekhouder, zaakvoerder…)',
    updated: 'Versie 1.0 — 25 september 2026',
    intro: 'Kort gezegd: als uw onderneming zich heeft geabonneerd op Aureus Social Pro, regelen deze voorwaarden UW persoonlijke account op het platform — niet het commerciële contract van uw onderneming (zie de Algemene Voorwaarden) en niet het werknemersportaal (zie de Gebruiksvoorwaarden werknemer).',
    s1_title: '1. Toepassingsgebied',
    s1_p1: 'Deze algemene gebruiksvoorwaarden (AGV) worden uitgegeven door Aureus IA SRL, KBO BE 1028.230.781, Square Eugène Plasky 92-94 bte 11, 1030 Schaarbeek, Brussel. E-mail: info@aureussocial.be.',
    s1_p2: 'Ze zijn van toepassing op elke natuurlijke persoon (« Gebruiker ») met een individuele account op app.aureussocial.be in het kader van zijn professionele activiteit voor rekening van een onderneming die zich heeft geabonneerd op de Dienst (de « Klant »), in de zin van de Algemene Voorwaarden. De Klant blijft de enige ondertekenaar van het commerciële contract; deze AGV regelen de relatie tussen Aureus en elke Gebruiker afzonderlijk.',
    s1_p3: 'Een werknemer van de Klant die enkel toegang heeft tot zijn eigen persoonlijke ruimte (loonfiches, verlof) valt onder de Gebruiksvoorwaarden — Werknemersportaal, niet onder deze AGV.',
    s2_title: '2. Account en toegang',
    s2_p1: 'De account van de Gebruiker wordt aangemaakt door een beheerder van de Klant, die zijn rol en machtigingen bepaalt (HR, boekhouder, alleen-lezen…). De Gebruiker is verantwoordelijk voor de vertrouwelijkheid van zijn inloggegevens en alle handelingen vanaf zijn account.',
    s2_p2: 'Tweefactorauthenticatie (2FA) is beschikbaar en sterk aanbevolen voor elke account met toegang tot gevoelige gegevens (RR-nummer, IBAN, lonen). Bij verlies van toegang of vermoeden van compromittering, contacteer onmiddellijk de beheerder van uw organisatie of info@aureussocial.be.',
    s2_p3: 'De machtigingen van de Gebruiker weerspiegelen de rol die hem door de Klant is toegekend. Aureus arbitreert de verdeling van rollen binnen de Klant niet: dit behoort tot de interne organisatie van de Klant.',
    s3_title: '3. Toegestaan gebruik',
    s3_li: [
      'Het platform gebruiken in het kader van zijn professionele opdracht voor rekening van de Klant',
      'Nauwkeurige en actuele gegevens invoeren, met name in de sociale aangiften',
      'De reikwijdte van de toegekende rol respecteren',
      'Elke werkings- of beveiligingsanomalie melden aan de beheerder van de Klant of aan Aureus',
    ],
    s4_title: '4. Verboden gebruik',
    s4_li: [
      'Beveiligingsmaatregelen omzeilen of trachten te omzeilen (2FA, dossierisolatie, logging)',
      'Een geantidateerde sociale aangifte indienen, of Aureus vragen dit te doen, of een aangifte waarvan de Gebruiker weet dat ze onjuist is — Artikel 9 van de Algemene Voorwaarden is hier identiek van toepassing',
      'Toegang zoeken of trachten te zoeken tot gegevens van een dossier waartoe de Gebruiker niet gemachtigd is, met inbegrip van andere Klanten',
      'Op grote schaal gegevens extraheren via een ander middel dan de exportfuncties van het platform',
      'Zijn inloggegevens delen met een derde, met inbegrip van een ander lid van de Klant',
      'Het platform gebruiken voor andere doeleinden dan de loon- en HR-administratie van de Klant',
    ],
    s5_title: '5. Intellectuele eigendom van de software',
    s5_p1: 'Het platform, de broncode, de architectuur, de berekeningsalgoritmen, de database, de interfaces en de documentatie blijven de exclusieve eigendom van Aureus IA SRL, beschermd door het Belgische auteursrecht (Boek XI van het Wetboek van economisch recht) en Richtlijn 2009/24/EG, automatisch verworven vanaf de creatie, zonder registratieformaliteit.',
    s5_p2: 'De aan de Gebruiker verleende toegang is een persoonlijk, niet-exclusief en niet-overdraagbaar gebruiksrecht, beperkt tot zijn opdracht voor rekening van de Klant. De Gebruiker mag de software niet reproduceren, decompileren, wijzigen of herverdelen, noch de broncode eruit halen.',
    s5_p3: 'Deze intellectuele eigendom van de software is te onderscheiden van de eigendom van de gegevens van de Klant (zie de Algemene Voorwaarden, Artikel 6): de ingevoerde gegevens zijn van de Klant; de software die ze verwerkt is van Aureus.',
    s6_title: '6. Persoonsgegevens van de Gebruiker',
    s6_p1: 'De verbindings- en gebruiksgegevens van de Gebruiker (identificatie, toegangslogboek) worden verwerkt conform het Privacybeleid. Voor de loongegevens die hij raadpleegt of invoert namens de Klant, is de Verwerkingsverantwoordelijke de Klant; Aureus treedt op als verwerker in de zin van Art. 28 AVG.',
    s6_p2: `Bewaring: het toegangs- en auditlogboek wordt bewaard volgens de bewaartermijn van de gegevens waarop het betrekking heeft — tot ${LB.conservation.fiscaleAns} jaar voor wat de fiscale en loongegevens betreft (Art. 315 WIB 1992, gewijzigd bij de wet van 20/11/2022).`,
    s7_title: '7. Beveiliging',
    s7_li: [
      'AES-256-GCM versleuteling van gevoelige gegevens (RR-nummer, IBAN)',
      'Sterke 2FA-authenticatie beschikbaar',
      'Data-isolatie per dossier (Row Level Security)',
      'Volledig toegangslogboek (audit trail)',
      'Dagelijkse versleutelde back-ups',
      'Exclusieve hosting Europese Unie (Frankfurt, Duitsland)',
    ],
    s8_title: '8. Beschikbaarheid',
    s8_p: `Aureus Social Pro streeft naar een beschikbaarheid van ${SLA_DISPONIBILITE} gemiddeld per maand (uitgezonderd geplande onderhoudsactiviteiten, 48u op voorhand per e-mail meegedeeld). Bij storing, contacteer de beheerder van uw organisatie of info@aureussocial.be.`,
    s9_title: '9. Opschorting en beëindiging van individuele toegang',
    s9_p1: 'De toegang van een Gebruiker kan op elk moment worden ingetrokken door een beheerder van de Klant (vertrek, rolwijziging) — deze intrekking beëindigt het contract van de Klant niet, dat geregeld wordt door de Algemene Voorwaarden.',
    s9_p2: 'Aureus kan de toegang van een Gebruiker opschorten, los van het contract van de Klant, bij bewezen frauduleus gebruik, ernstige schending van deze AGV of poging tot ongeautoriseerde toegang tot een ander dossier, na melding aan de beheerder van de Klant voor zover mogelijk.',
    s10_title: '10. Wijzigingen',
    s10_p: 'Deze AGV kunnen worden gewijzigd om wettelijke of technische evoluties weer te geven. Elke substantiële wijziging wordt per e-mail meegedeeld aan de beheerders van de Klant, minstens 30 dagen vóór inwerkingtreding.',
    s11_title: '11. Klachten en toepasselijk recht',
    s11_p1: 'Klacht: wend u tot de beheerder van uw organisatie of tot info@aureussocial.be. Antwoord binnen 30 dagen maximum.',
    s11_p2: 'Gegevensbeschermingsautoriteit (GBA): Drukpersstraat 35, 1000 Brussel · +32 2 274 48 00 · contact@apd-gba.be.',
    s11_p3: 'Toepasselijk recht: Belgisch recht. Rechtbanken van het gerechtelijk arrondissement Brussel.',
    footer: 'Document gegenereerd op 25 september 2026 — Versie 1.0',
  },
};

export default function CGUPage() {
  const [lang, setLang] = useLangueArrivee(Object.keys(CONTENT));

  const t = CONTENT[lang];

  const S = {
    h1: { fontSize: 26, fontWeight: 800, color: '#c6a34e', margin: '0 0 8px', letterSpacing: '-0.5px' },
    subtitle: { fontSize: 12, color: '#888', margin: '0 0 24px', lineHeight: 1.6 },
    section: { marginBottom: 28 },
    h2: { fontSize: 16, fontWeight: 700, color: '#e8e6e0', margin: '0 0 12px', paddingBottom: 8, borderBottom: '1px solid rgba(198,163,78,.12)' },
    p: { fontSize: 13, color: '#b0ada6', lineHeight: 1.8, margin: '0 0 10px' },
    updated: { fontSize: 10, color: '#666', marginBottom: 32, fontStyle: 'italic' },
    box: { background: 'rgba(198,163,78,.05)', border: '1px solid rgba(198,163,78,.15)', borderRadius: 8, padding: 16, margin: '12px 0', fontSize: 12, color: '#c6a34e' },
    li: { fontSize: 13, color: '#b0ada6', lineHeight: 1.8, marginLeft: 20 },
    langSelector: { display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' },
    langBtn: (active) => ({
      padding: '6px 14px', borderRadius: 6, border: `1px solid ${active ? '#c6a34e' : 'rgba(198,163,78,.2)'}`,
      background: active ? 'rgba(198,163,78,.15)' : 'transparent', color: active ? '#c6a34e' : '#888',
      cursor: 'pointer', fontSize: 11, fontWeight: 600, fontFamily: 'inherit',
    }),
  };

  return (
    <div>
      <div style={S.langSelector}>
        {['fr', 'nl'].map(l => (
          <button key={l} onClick={() => setLang(l)} style={S.langBtn(lang === l)}>{l.toUpperCase()}</button>
        ))}
      </div>

      <h1 style={S.h1}>{t.title}</h1>
      <p style={S.subtitle}>{t.subtitle}</p>
      <p style={S.updated}>{t.updated}</p>

      <div style={S.box}><strong>{t.intro}</strong></div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s1_title}</h2>
        <p style={S.p}>{t.s1_p1}</p>
        <p style={S.p}>{t.s1_p2}</p>
        <p style={S.p}>{t.s1_p3}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s2_title}</h2>
        <p style={S.p}>{t.s2_p1}</p>
        <p style={S.p}>{t.s2_p2}</p>
        <p style={S.p}>{t.s2_p3}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s3_title}</h2>
        <ul>{t.s3_li.map((l, i) => <li key={i} style={S.li}>{l}</li>)}</ul>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s4_title}</h2>
        <ul>{t.s4_li.map((l, i) => <li key={i} style={S.li}>{l}</li>)}</ul>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s5_title}</h2>
        <p style={S.p}>{t.s5_p1}</p>
        <p style={S.p}>{t.s5_p2}</p>
        <p style={S.p}>{t.s5_p3}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s6_title}</h2>
        <p style={S.p}>{t.s6_p1}</p>
        <p style={S.p}>{t.s6_p2}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s7_title}</h2>
        <ul>{t.s7_li.map((l, i) => <li key={i} style={S.li}>{l}</li>)}</ul>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s8_title}</h2>
        <p style={S.p}>{t.s8_p}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s9_title}</h2>
        <p style={S.p}>{t.s9_p1}</p>
        <p style={S.p}>{t.s9_p2}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s10_title}</h2>
        <p style={S.p}>{t.s10_p}</p>
      </div>

      <div style={S.section}>
        <h2 style={S.h2}>{t.s11_title}</h2>
        <p style={S.p}>{t.s11_p1}</p>
        <p style={S.p}>{t.s11_p2}</p>
        <p style={S.p}>{t.s11_p3}</p>
      </div>

      <div style={{ marginTop: 40, padding: 20, border: '1px solid rgba(198,163,78,.2)', borderRadius: 8, textAlign: 'center', fontSize: 11, color: '#888' }}>
        Aureus IA SRL — BCE BE 1028.230.781 — info@aureussocial.be<br />
        <em>{t.footer}</em>
      </div>
    </div>
  );
}
